import React, { useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo } from 'react';
import { Modal, Button, Spin, message, Tooltip } from 'antd';
import { useTableChanges } from '../hooks/useWebSocket';
import { CloseOutlined, DownloadOutlined, CompressOutlined, ExpandOutlined, ArrowRightOutlined, DeleteOutlined, CommentOutlined, EditOutlined, MenuFoldOutlined, MenuUnfoldOutlined, EyeOutlined, EyeInvisibleOutlined, ZoomInOutlined, ZoomOutOutlined } from '@ant-design/icons';
import CommentEditor from '../components/attachment/CommentEditor';
import { Document, Page } from 'react-pdf';
import { useTranslation } from 'react-i18next';
import axios from '../utility/axios';
import { pdfjs } from 'react-pdf';
import { useAuth } from '../hooks/useAuth';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import '../styles/contexts/AttachmentPreview.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const VALID_COMMENT_SOURCES = ['messages', 'documenso', 'document'];
const COMMENT_DEFAULTS = { width: 25, height: 10 };
const POSTIT_MOVE_SIZE = { width: 10, height: 8 };

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 3;
const ZOOM_STEP = 0.2;

/* Author color palette: distinct, accessible pastels for thread identification - see-through */
const AUTHOR_COLORS = [
  { bg: 'rgba(24, 144, 255, 0.12)', border: '#1890ff', accent: '#1890ff' },
  { bg: 'rgba(82, 196, 26, 0.12)', border: '#52c41a', accent: '#52c41a' },
  { bg: 'rgba(250, 173, 20, 0.15)', border: '#faad14', accent: '#d48806' },
  { bg: 'rgba(245, 34, 45, 0.12)', border: '#f5222d', accent: '#cf1322' },
  { bg: 'rgba(114, 46, 209, 0.12)', border: '#722ed1', accent: '#722ed1' },
  { bg: 'rgba(13, 202, 240, 0.12)', border: '#0dcaf0', accent: '#0891b2' },
  { bg: 'rgba(255, 77, 79, 0.12)', border: '#ff4d4f', accent: '#ff7875' },
  { bg: 'rgba(19, 194, 194, 0.12)', border: '#13c2c2', accent: '#36cfc9' },
  { bg: 'rgba(250, 84, 28, 0.12)', border: '#fa541c', accent: '#fa8c16' },
  { bg: 'rgba(114, 46, 209, 0.1)', border: '#9254de', accent: '#b37feb' },
];

const hashAuthor = (str) => {
  if (!str || typeof str !== 'string') return 0;
  let h = 0;
  for (let i = 0; i < str.length; i++) h = ((h << 5) - h) + str.charCodeAt(i) | 0;
  return Math.abs(h);
};

const getAuthorColor = (comment) => {
  const key = comment?.author_email || comment?.author || 'anonymous';
  const idx = hashAuthor(String(key).toLowerCase()) % AUTHOR_COLORS.length;
  return AUTHOR_COLORS[idx];
};

const isContentEmpty = (html) => !html || (typeof html === 'string' && html.replace(/<[^>]*>/g, '').trim() === '');

const findInTree = (list, id) => {
  if (!list) return null;
  for (const c of list) {
    if (c.unique_identifier === id) return c;
    const found = findInTree(c.replies, id);
    if (found) return found;
  }
  return null;
};

const filterFromTree = (tree, id) =>
  tree.filter((c) => c.unique_identifier !== id).map((c) => ({ ...c, replies: filterFromTree(c.replies || [], id) }));

const countReplies = (c) => (c?.replies?.length ?? 0) + (c?.replies ?? []).reduce((n, r) => n + countReplies(r), 0);

const stripHtml = (html) => (typeof html === 'string' ? html.replace(/<[^>]*>/g, '').trim() : '');

const flattenReplies = (c, out = []) => {
  (c?.replies ?? []).forEach((r) => { out.push(r); flattenReplies(r, out); });
  return out;
};

const renderThreadContent = (thread, borderColor) => {
  const renderItem = (c, depth) => (
    <div
      key={c.unique_identifier}
      className="attachment-preview-postit-item"
      style={{ paddingLeft: depth * 14, ...(depth === 0 ? { borderLeft: 'none' } : {}) }}
    >
      <div
        className="attachment-preview-postit-content"
        style={depth === 0 ? { color: borderColor } : undefined}
        dangerouslySetInnerHTML={{ __html: c.content || '' }}
      />
      {depth > 0 && (
        <div className="attachment-preview-postit-author">
          {(c.author_email || c.author || 'Anonymous').split('@')[0]}
        </div>
      )}
      {(c.replies ?? []).map((r) => renderItem(r, depth + 1))}
    </div>
  );
  return (
    <div className="attachment-preview-postit-body">
      {renderItem(thread, 0)}
    </div>
  );
};

const AttachmentPreview = ({
  open,
  onClose,
  documentFileId,
  fileName,
  onDownload,
  source = 'document',
  localFile,
  localComments,
  onLocalCommentsChange,
  draftFileId,
  draftDownloadUrl,
  draftFileComments,
  onDraftFileCommentsChange,
  linkId,
}) => {
  const { t } = useTranslation();
  const { user, isLoggedIn } = useAuth();
  const [viewerCanAddComments, setViewerCanAddComments] = useState(null);
  const [viewerEmail, setViewerEmail] = useState(null);
  const currentUserEmail = user?.email ?? viewerEmail ?? null;
  const [pdfData, setPdfData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [minimized, setMinimized] = useState(false);
  const [pageDimensions, setPageDimensions] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentDraft, setCommentDraft] = useState('');
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [placingComment, setPlacingComment] = useState(false);
  const [pendingPlacement, setPendingPlacement] = useState(null);
  const [cursorPreview, setCursorPreview] = useState(null);
  const [dragPreview, setDragPreview] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [selectedPlacement, setSelectedPlacement] = useState(false);
  const [resizing, setResizing] = useState(null);
  const [moving, setMoving] = useState(false);
  const [selectedCommentId, setSelectedCommentId] = useState(null);
  const [editingDraft, setEditingDraft] = useState(null);
  const [replyingToCommentId, setReplyingToCommentId] = useState(null);
  const [replyDraft, setReplyDraft] = useState('');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [postitsVisible, setPostitsVisible] = useState(true);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [fitContentWidth, setFitContentWidth] = useState(0);
  const commentsPanelRef = useRef(null);
  const threadRefsMap = useRef({});
  const pdfAreaRef = useRef(null);
  const pageWrapperRef = useRef(null);
  const editingInputRef = useRef(null);
  const draggingCommentRef = useRef(false);
  const drawStartRef = useRef(null);
  const resizeStartRef = useRef(null);
  const moveStartRef = useRef(null);
  const placeholderInputRef = useRef(null);
  const markerMovedRef = useRef(false);
  const pdfBlobUrlRef = useRef(null);
  const pageDimensionsRef = useRef(null);
  const didFocusPlaceholderRef = useRef(false);
  const rafScheduledRef = useRef(false);
  const pendingCursorRef = useRef(null);
  const pendingPositionRef = useRef(null);
  const pendingPlacementRef = useRef(null);
  const pendingResizeRef = useRef(null);
  const cursorPreviewRef = useRef(null);
  const updateCommentPositionRef = useRef(null);
  const updatePendingPlacementRef = useRef(null);
  const updateEditingDraftRef = useRef(null);

  const isDraftFile = !!draftFileId && !!draftDownloadUrl;
  const isLocalFile = !!localFile && !documentFileId && !isDraftFile;
  const hasSource = documentFileId || localFile || isDraftFile;
  const displayFileName = fileName || (localFile && (localFile.originFileObj?.name || localFile.name)) || (isDraftFile && fileName) || '';
  const useLocalComments = isLocalFile && typeof onLocalCommentsChange === 'function';
  const useDraftFileComments = isDraftFile && typeof onDraftFileCommentsChange === 'function';
  const hasDocumentFileComments = !!documentFileId && VALID_COMMENT_SOURCES.includes(source);
  const canViewComments = useLocalComments || useDraftFileComments || hasDocumentFileComments;
  const canComment =
    (useLocalComments || useDraftFileComments) ||
    (hasDocumentFileComments && (isLoggedIn || viewerCanAddComments === true));
  const canReplyToComment = canComment;
  const canEditComment = useCallback((c) => {
    if (useLocalComments || useDraftFileComments) return true;
    if (!currentUserEmail || !c?.author_email) return false;
    return String(c.author_email).toLowerCase() === String(currentUserEmail).toLowerCase();
  }, [useLocalComments, useDraftFileComments, currentUserEmail]);

  const handleZoom = useCallback((delta) => {
    setZoomLevel((z) => Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, z + delta)));
  }, []);

  const measurePdfAreaWidth = useCallback(() => {
    const el = pdfAreaRef.current;
    if (!el) return;
    const cs = window.getComputedStyle(el);
    const pl = parseFloat(cs.paddingLeft) || 0;
    const pr = parseFloat(cs.paddingRight) || 0;
    const w = Math.floor(el.clientWidth - pl - pr);
    if (w > 0) setFitContentWidth(w);
  }, []);

  const pageRenderWidth = useMemo(() => {
    const base = fitContentWidth > 0
      ? fitContentWidth
      : (typeof window !== 'undefined' ? Math.max(120, Math.min(900, window.innerWidth - 100)) : 800);
    return Math.max(120, Math.round(base * zoomLevel));
  }, [fitContentWidth, zoomLevel]);

  useEffect(() => {
    if (open) {
      setSidebarCollapsed(true);
    }
  }, [open]);

  useEffect(() => {
    if (!hasDocumentFileComments) {
      setViewerCanAddComments(null);
      setViewerEmail(null);
      return;
    }
    if (isLoggedIn) {
      setViewerCanAddComments(true);
      setViewerEmail(null);
      return;
    }
    let cancelled = false;
    fetch('/api/contacts-mailbox/status/', { credentials: 'include' })
      .then((res) => res.json())
      .then((data) => {
        if (!cancelled) {
          setViewerCanAddComments(data.is_free_account === true);
          setViewerEmail(data.receiver_email || null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setViewerCanAddComments(false);
          setViewerEmail(null);
        }
      });
    return () => { cancelled = true; };
  }, [hasDocumentFileComments, isLoggedIn, user?.email, open]);

  useEffect(() => {
    if (!open || !hasSource) {
      const closeDelayMs = 320;
      const timer = setTimeout(() => {
        pageDimensionsRef.current = null;
        setPdfData(null);
        setError(null);
        setNumPages(null);
        setCurrentPage(1);
        setPageDimensions(null);
        setMinimized(false);
        setComments([]);
        setCommentDraft('');
        setPlacingComment(false);
        setPendingPlacement(null);
        setCursorPreview(null);
        setDragPreview(null);
        setIsDrawing(false);
        setSelectedPlacement(false);
        setResizing(null);
        setMoving(false);
        setSelectedCommentId(null);
        setEditingDraft(null);
        setReplyingToCommentId(null);
        setReplyDraft('');
        setSidebarCollapsed(false);
        setPostitsVisible(true);
        setViewerCanAddComments(null);
        setViewerEmail(null);
        setZoomLevel(1);
        setFitContentWidth(0);
      }, closeDelayMs);
      return () => clearTimeout(timer);
    }

    setMinimized(false);
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        let arrayBuffer;
        if (isDraftFile) {
          const res = await axios.get(draftDownloadUrl, { responseType: 'arraybuffer' });
          if (cancelled) return;
          const contentType = res.headers?.['content-type'] || 'application/octet-stream';
          const name = fileName || 'draft-file';
          const isPdf = contentType === 'application/pdf' || name.toLowerCase().endsWith('.pdf');
          if (isPdf) {
            arrayBuffer = res.data;
          } else {
            const blob = new Blob([res.data], { type: contentType });
            const file = new File([blob], name, { type: contentType });
            const fd = new FormData();
            fd.append('file', file, name);
            const convRes = await axios.post('/api/docuso/convert-for-preview/', fd, { responseType: 'arraybuffer' });
            if (cancelled) return;
            arrayBuffer = convRes.data;
          }
        } else if (isLocalFile) {
          const file = localFile.originFileObj || localFile;
          const isPdf = file.type === 'application/pdf' || (file.name && file.name.toLowerCase().endsWith('.pdf'));
          if (isPdf) {
            arrayBuffer = await file.arrayBuffer();
          } else {
            const fd = new FormData();
            fd.append('file', file, file.name);
            const res = await axios.post('/api/docuso/convert-for-preview/', fd, { responseType: 'arraybuffer' });
            if (cancelled) return;
            arrayBuffer = res.data;
          }
        } else {
          const previewUrl = source === 'documenso' && linkId
            ? `/api/docuso/link/${linkId}/download/${documentFileId}/`
            : source === 'documenso'
            ? `/api/docuso/download/${documentFileId}/`
            : source === 'messages'
            ? `/api/messages/preview-pdf/${documentFileId}/`
            : `/api/documents/preview-pdf/${documentFileId}/`;
          const res = await axios.get(previewUrl, {
            responseType: 'arraybuffer',
          });
          if (cancelled) return;
          arrayBuffer = res.data;
        }
        if (cancelled) return;
        if (pdfBlobUrlRef.current) {
          URL.revokeObjectURL(pdfBlobUrlRef.current);
          pdfBlobUrlRef.current = null;
        }
        const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        pdfBlobUrlRef.current = url;
        setZoomLevel(1);
        setPdfData(url);
      } catch (err) {
        if (!cancelled) {
          const status = err.response?.status;
          const data = err.response?.data;
          let errMsg = err.message;
          if (data && typeof data === 'object' && typeof data.error === 'string') errMsg = data.error;
          else if (typeof data === 'string') errMsg = data;
          else if (data instanceof ArrayBuffer) {
            try {
              const parsed = JSON.parse(new TextDecoder().decode(data));
              if (parsed?.error) errMsg = parsed.error;
            } catch (_) {}
          }
          setError({ status, message: errMsg });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
      if (pdfBlobUrlRef.current) {
        URL.revokeObjectURL(pdfBlobUrlRef.current);
        pdfBlobUrlRef.current = null;
      }
    };
  }, [open, documentFileId, source, localFile, isLocalFile, hasSource, isDraftFile, draftFileId, draftDownloadUrl, fileName, linkId]);

  const onDocumentLoadSuccess = useCallback(({ numPages: n }) => {
    setNumPages(n);
    setCurrentPage(1);
    requestAnimationFrame(() => {
      if (pdfAreaRef.current) {
        pdfAreaRef.current.scrollTop = 0;
        pdfAreaRef.current.scrollLeft = 0;
      }
    });
  }, []);

  const measurePageDimensions = useCallback(() => {
    if (pageWrapperRef.current && pdfData) {
      const rect = pageWrapperRef.current.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        const w = Math.round(rect.width);
        const h = Math.round(rect.height);
        if (!pageDimensionsRef.current || pageDimensionsRef.current.width !== w || pageDimensionsRef.current.height !== h) {
          pageDimensionsRef.current = { width: w, height: h };
          setPageDimensions({ width: w, height: h });
        }
      }
    }
  }, [pdfData]);

  const fileConfig = useMemo(() => (pdfData ? { url: pdfData } : null), [pdfData]);

  const commentTree = useMemo(() => {
    if (comments.some((c) => Array.isArray(c.replies))) return comments;
    const topLevel = comments.filter((c) => !c.parent_comment_id);
    const byParent = {};
    comments.forEach((c) => {
      if (c.parent_comment_id) {
        const pid = c.parent_comment_id;
        if (!byParent[pid]) byParent[pid] = [];
        byParent[pid].push(c);
      }
    });
    const buildReplies = (parentId) =>
      (byParent[parentId] || []).sort((a, b) => (a.created_at || '').localeCompare(b.created_at || '')).map((c) => ({ ...c, replies: buildReplies(c.unique_identifier) }));
    return topLevel.map((c) => ({ ...c, replies: buildReplies(c.unique_identifier) }));
  }, [comments]);

  useLayoutEffect(() => {
    if (!pdfData || error || loading) return;
    measurePdfAreaWidth();
  }, [pdfData, error, loading, measurePdfAreaWidth, sidebarCollapsed, minimized, open]);

  useEffect(() => {
    if (!pdfData || error || loading) return;
    const el = pdfAreaRef.current;
    const ro = el && typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => measurePdfAreaWidth())
      : null;
    if (ro && el) ro.observe(el);
    window.addEventListener('resize', measurePdfAreaWidth);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', measurePdfAreaWidth);
    };
  }, [pdfData, error, loading, measurePdfAreaWidth, sidebarCollapsed, minimized, open]);

  useEffect(() => {
    const el = pdfAreaRef.current;
    if (!el || !open || !pdfData || loading || error) return;
    const onWheel = (e) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
        handleZoom(delta);
      }
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [open, pdfData, loading, error, handleZoom]);

  useEffect(() => {
    const el = pdfAreaRef.current;
    if (!el) return;
    el.scrollTop = 0;
    el.scrollLeft = 0;
  }, [currentPage]);

  useEffect(() => {
    if (!pdfData || error || loading) return;
    const runMeasure = () => {
      measurePageDimensions();
    };
    const timer = setTimeout(runMeasure, 500);
    const resizeTimer = setTimeout(runMeasure, 800);
    const ro = pageWrapperRef.current && typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => setTimeout(runMeasure, 100))
      : null;
    if (ro && pageWrapperRef.current) ro.observe(pageWrapperRef.current);
    const onResize = () => runMeasure();
    window.addEventListener('resize', onResize);
    return () => {
      clearTimeout(timer);
      clearTimeout(resizeTimer);
      ro?.disconnect();
      window.removeEventListener('resize', onResize);
    };
  }, [pdfData, error, loading, currentPage, measurePageDimensions, zoomLevel, fitContentWidth, pageRenderWidth]);

  useEffect(() => {
    if (minimized) setSidebarCollapsed(true);
  }, [minimized]);

  useEffect(() => {
    if (!open) return;
    if (useLocalComments) {
      setComments(Array.isArray(localComments) ? localComments : []);
      setCommentsLoading(false);
      return;
    }
    if (useDraftFileComments) {
      setComments(Array.isArray(draftFileComments) ? draftFileComments : []);
      setCommentsLoading(false);
      return;
    }
    if (!hasDocumentFileComments) return;
    let cancelled = false;
    setCommentsLoading(true);
    axios.get('/api/attachment-preview/comments/', { params: { attachment_id: documentFileId, source } })
      .then((res) => {
        if (!cancelled) setComments(res.data.comments || []);
      })
      .catch(() => {
        if (!cancelled) setComments([]);
      })
      .finally(() => {
        if (!cancelled) setCommentsLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, hasDocumentFileComments, documentFileId, source, useLocalComments, localComments, useDraftFileComments, draftFileComments]);

  const fetchCommentsRef = useRef(null);
  fetchCommentsRef.current = () => {
    if (!hasDocumentFileComments || !documentFileId || !source) return;
    axios.get('/api/attachment-preview/comments/', { params: { attachment_id: documentFileId, source } })
      .then((res) => setComments(res.data.comments || []))
      .catch(() => setComments([]));
  };

  const handleCommentTableChange = useCallback((change) => {
    if (change.table !== 'attachment_comments') return;
    if (!documentFileId || !hasDocumentFileComments) return;
    const aid = change.data?.attachment_id;
    if (!aid || String(aid) !== String(documentFileId)) return;
    fetchCommentsRef.current?.();
  }, [documentFileId, hasDocumentFileComments]);

  useTableChanges('attachment_comments', handleCommentTableChange, open && hasDocumentFileComments);

  const getPositionFromEvent = useCallback((e) => {
    const el = pageWrapperRef.current;
    if (!el) return null;
    const r = el.getBoundingClientRect();
    const x = (e.clientX ?? e.touches?.[0]?.clientX ?? 0) - r.left;
    const y = (e.clientY ?? e.touches?.[0]?.clientY ?? 0) - r.top;
    const position_x = Math.max(0, Math.min(100, (x / r.width) * 100));
    const position_y = Math.max(0, Math.min(100, (y / r.height) * 100));
    return { position_x, position_y };
  }, []);

  const placeCommentBox = useCallback((position_x, position_y, width, height) => {
    const placement = {
      page: currentPage,
      position_x,
      position_y,
      width: Math.max(5, Math.min(90, width)),
      height: Math.max(3, Math.min(50, height)),
    };
    setPendingPlacement(placement);
    setPlacingComment(false);
    setSelectedPlacement(true);
  }, [currentPage]);

  useEffect(() => {
    if (pendingPlacement) {
      if (!didFocusPlaceholderRef.current) {
        didFocusPlaceholderRef.current = true;
        const timer = setTimeout(() => placeholderInputRef.current?.focus?.(), 50);
        return () => clearTimeout(timer);
      }
    } else {
      didFocusPlaceholderRef.current = false;
    }
  }, [pendingPlacement]);

  useEffect(() => {
    if (editingDraft && selectedCommentId) {
      const timer = setTimeout(() => editingInputRef.current?.focus(), 50);
      return () => clearTimeout(timer);
    }
  }, [editingDraft, selectedCommentId]);

  const updatePendingPlacement = useCallback((updates) => {
    setPendingPlacement((prev) => (prev ? { ...prev, ...updates } : null));
  }, []);

  const updateEditingDraft = useCallback((updates) => {
    setEditingDraft((prev) => (prev ? { ...prev, ...updates } : null));
  }, []);

  const updateCommentPosition = useCallback((commentId, updates) => {
    if (useLocalComments) {
      const list = Array.isArray(localComments) ? localComments : [];
      onLocalCommentsChange(list.map((c) => (c.unique_identifier === commentId ? { ...c, ...updates } : c)));
      return;
    }
    if (useDraftFileComments) {
      const list = Array.isArray(draftFileComments) ? draftFileComments : [];
      onDraftFileCommentsChange(list.map((c) => (c.unique_identifier === commentId ? { ...c, ...updates } : c)));
      return;
    }
    const updateInTree = (tree, id, u) =>
      tree.map((c) => (c.unique_identifier === id ? { ...c, ...u } : { ...c, replies: updateInTree(c.replies || [], id, u) }));
    setComments((prev) => updateInTree(prev, commentId, updates));
  }, [useLocalComments, localComments, onLocalCommentsChange, useDraftFileComments, draftFileComments, onDraftFileCommentsChange]);

  useEffect(() => {
    updateCommentPositionRef.current = updateCommentPosition;
    updatePendingPlacementRef.current = updatePendingPlacement;
    updateEditingDraftRef.current = updateEditingDraft;
  }, [updateCommentPosition, updatePendingPlacement, updateEditingDraft]);

  cursorPreviewRef.current = cursorPreview;

  const flushRafUpdates = useCallback(() => {
    if (pendingCursorRef.current !== undefined) {
      setCursorPreview(pendingCursorRef.current);
      pendingCursorRef.current = undefined;
    }
    const pos = pendingPositionRef.current;
    if (pos && updateCommentPositionRef.current) {
      updateCommentPositionRef.current(pos.commentId, pos.updates);
      pendingPositionRef.current = null;
    }
    const pl = pendingPlacementRef.current;
    if (pl) {
      const fn = pl.isEditing ? updateEditingDraftRef.current : updatePendingPlacementRef.current;
      if (fn) fn(pl.updates);
      pendingPlacementRef.current = null;
    }
    const rs = pendingResizeRef.current;
    if (rs) {
      const fn = rs.isEditing ? updateEditingDraftRef.current : updatePendingPlacementRef.current;
      if (fn) fn(rs.updates);
      pendingResizeRef.current = null;
    }
  }, []);

  const scheduleRafFlush = useCallback(() => {
    if (rafScheduledRef.current) return;
    rafScheduledRef.current = true;
    requestAnimationFrame(() => {
      rafScheduledRef.current = false;
      flushRafUpdates();
    });
  }, [flushRafUpdates]);

  const persistCommentPosition = useCallback((commentId, position_x, position_y, width, height) => {
    const comment = comments.find((c) => c.unique_identifier === commentId) || comments.flatMap((c) => c.replies || []).find((r) => r.unique_identifier === commentId);
    if (!comment || useLocalComments || useDraftFileComments) return Promise.resolve();
    const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(commentId);
    if (!isUuid) return Promise.resolve();
    return axios.put(`/api/attachment-preview/comments/${commentId}/`, {
      content: comment.content,
      page: comment.page,
      position_x,
      position_y,
      width: width ?? comment.width,
      height: height ?? comment.height,
    }, { headers: { 'Content-Type': 'application/json' } })
      .then((res) => {
        const updateInTree = (tree, id, val) =>
          tree.map((c) => (c.unique_identifier === id ? { ...val, replies: c.replies } : { ...c, replies: updateInTree(c.replies || [], id, val) }));
        setComments((prev) => updateInTree(prev, commentId, res.data.comment));
      })
      .catch((err) => { message.error(err.response?.data?.error || err.message); });
  }, [comments, useLocalComments, useDraftFileComments]);

  const persistEditingDraft = useCallback((options = {}) => {
    const { closeOnSuccess = false, silent = false } = options;
    if (!editingDraft || !selectedCommentId || commentSubmitting) return Promise.resolve();
    const content = typeof editingDraft.content === 'string' ? editingDraft.content.trim() : editingDraft.content;
    if (isContentEmpty(content)) return Promise.resolve();
    const comment = findInTree(comments, selectedCommentId);
    if (!comment) return Promise.resolve();

    if (useLocalComments) {
      const updated = comments.map((c) =>
        c.unique_identifier === selectedCommentId
          ? { ...c, content, position_x: editingDraft.position_x, position_y: editingDraft.position_y, width: editingDraft.width, height: editingDraft.height }
          : c
      );
      onLocalCommentsChange(updated);
      if (closeOnSuccess) {
        setSelectedCommentId(null);
        setEditingDraft(null);
      }
      if (!silent) message.success(t('common.saved') || 'Saved');
      return Promise.resolve();
    }
    if (useDraftFileComments) {
      const updated = comments.map((c) =>
        c.unique_identifier === selectedCommentId
          ? { ...c, content, position_x: editingDraft.position_x, position_y: editingDraft.position_y, width: editingDraft.width, height: editingDraft.height }
          : c
      );
      onDraftFileCommentsChange(updated);
      if (closeOnSuccess) {
        setSelectedCommentId(null);
        setEditingDraft(null);
      }
      if (!silent) message.success(t('common.saved') || 'Saved');
      return Promise.resolve();
    }

    const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(selectedCommentId);
    if (!isUuid) return Promise.resolve();

    setCommentSubmitting(true);
    return axios.put(`/api/attachment-preview/comments/${selectedCommentId}/`, {
      content,
      page: comment.page,
      position_x: editingDraft.position_x,
      position_y: editingDraft.position_y,
      width: editingDraft.width,
      height: editingDraft.height,
    }, { headers: { 'Content-Type': 'application/json' } })
      .then((res) => {
        const updateInTree = (tree, id, val) =>
          tree.map((c) => (c.unique_identifier === id ? { ...val, replies: c.replies } : { ...c, replies: updateInTree(c.replies || [], id, val) }));
        setComments((prev) => updateInTree(prev, selectedCommentId, res.data.comment));
        if (closeOnSuccess) {
          setSelectedCommentId(null);
          setEditingDraft(null);
        }
        if (!silent) message.success(t('common.saved') || 'Saved');
      })
      .catch((err) => {
        const msg = err.response?.data?.error || err.message;
        message.error(msg);
      })
      .finally(() => setCommentSubmitting(false));
  }, [editingDraft, selectedCommentId, commentSubmitting, comments, useLocalComments, useDraftFileComments, onLocalCommentsChange, onDraftFileCommentsChange, t]);

  const handlePageMouseDown = useCallback((e) => {
    if (e.target.closest('.attachment-preview-comment-postit-wrapper, .attachment-preview-comment-placeholder, .attachment-preview-resize-handle')) return;
    setSelectedPlacement(false);
    setSelectedCommentId(null);
    setEditingDraft(null);
    if (!placingComment || !canComment || !pageWrapperRef.current) return;
    const pos = getPositionFromEvent(e);
    if (pos) {
      drawStartRef.current = { ...pos };
      setIsDrawing(true);
    }
  }, [placingComment, canComment, getPositionFromEvent]);

  const handlePageMouseMove = useCallback((e) => {
    const pageEl = pageWrapperRef.current;
    if (!pageEl || !canComment) return;
    const ms = moveStartRef.current;
    if (ms?.movingMarkerId && !isDrawing && !resizing) {
      const { startMouseX, startMouseY, startPosX, startPosY, markerWidth, markerHeight } = ms;
      const curr = getPositionFromEvent(e);
      if (!curr) return;
      const deltaX = curr.position_x - startMouseX;
      const deltaY = curr.position_y - startMouseY;
      const maxX = 100 - (markerWidth ?? COMMENT_DEFAULTS.width);
      const maxY = 100 - (markerHeight ?? COMMENT_DEFAULTS.height);
      const posX = Math.max(0, Math.min(maxX, startPosX + deltaX));
      const posY = Math.max(0, Math.min(maxY, startPosY + deltaY));
      if (Math.abs(posX - startPosX) > 0.5 || Math.abs(posY - startPosY) > 0.5) {
        markerMovedRef.current = true;
      }
      moveStartRef.current.lastPosX = posX;
      moveStartRef.current.lastPosY = posY;
      pendingPositionRef.current = { commentId: ms.movingMarkerId, updates: { position_x: posX, position_y: posY } };
      scheduleRafFlush();
      return;
    }
    const activePlacement = ms?.editing ? editingDraft : pendingPlacement;
    if (ms && activePlacement && !isDrawing && !resizing) {
      const { startMouseX, startMouseY, startPosX, startPosY } = ms;
      const curr = getPositionFromEvent(e);
      if (!curr) return;
      const deltaX = curr.position_x - startMouseX;
      const deltaY = curr.position_y - startMouseY;
      const maxX = 100 - activePlacement.width;
      const maxY = 100 - activePlacement.height;
      const posX = Math.max(0, Math.min(maxX, startPosX + deltaX));
      const posY = Math.max(0, Math.min(maxY, startPosY + deltaY));
      pendingPlacementRef.current = { updates: { position_x: posX, position_y: posY }, isEditing: !!ms?.editing };
      scheduleRafFlush();
      return;
    }
    const activeResizeTarget = resizeStartRef.current?.editing ? editingDraft : pendingPlacement;
    const updateResizeTarget = resizeStartRef.current?.editing ? updateEditingDraft : updatePendingPlacement;
    if (resizing && resizeStartRef.current && activeResizeTarget) {
      const { corner, startPosX, startPosY, startW, startH } = resizeStartRef.current;
      const curr = getPositionFromEvent(e);
      if (!curr) return;
      let posX = startPosX;
      let posY = startPosY;
      let w = startW;
      let h = startH;
      if (corner === 'br') {
        w = Math.max(5, Math.min(90, curr.position_x - startPosX));
        h = Math.max(3, Math.min(50, curr.position_y - startPosY));
      } else if (corner === 'bl') {
        posX = Math.max(0, Math.min(startPosX + startW - 5, curr.position_x));
        w = Math.max(5, Math.min(90, startPosX + startW - curr.position_x));
        h = Math.max(3, Math.min(50, curr.position_y - startPosY));
      } else if (corner === 'tr') {
        posY = Math.max(0, Math.min(startPosY + startH - 3, curr.position_y));
        w = Math.max(5, Math.min(90, curr.position_x - startPosX));
        h = Math.max(3, Math.min(50, startPosY + startH - curr.position_y));
      } else if (corner === 'tl') {
        posX = Math.max(0, Math.min(startPosX + startW - 5, curr.position_x));
        posY = Math.max(0, Math.min(startPosY + startH - 3, curr.position_y));
        w = Math.max(5, Math.min(90, startPosX + startW - curr.position_x));
        h = Math.max(3, Math.min(50, startPosY + startH - curr.position_y));
      }
      pendingResizeRef.current = { updates: { position_x: posX, position_y: posY, width: w, height: h }, isEditing: !!resizeStartRef.current?.editing };
      scheduleRafFlush();
      resizeStartRef.current = { ...resizeStartRef.current, startPosX: posX, startPosY: posY, startW: w, startH: h };
      return;
    }
    if (isDrawing && drawStartRef.current) {
      const start = drawStartRef.current;
      const curr = getPositionFromEvent(e);
      if (!curr) return;
      const posX = Math.min(start.position_x, curr.position_x);
      const posY = Math.min(start.position_y, curr.position_y);
      const w = Math.abs(curr.position_x - start.position_x);
      const h = Math.abs(curr.position_y - start.position_y);
      pendingCursorRef.current = {
        position_x: posX,
        position_y: posY,
        width: Math.max(2, w),
        height: Math.max(2, h),
      };
      scheduleRafFlush();
      return;
    }
    if (placingComment) {
      const pos = getPositionFromEvent(e);
      if (pos) {
        pendingCursorRef.current = {
          ...pos,
          width: COMMENT_DEFAULTS.width,
          height: COMMENT_DEFAULTS.height,
        };
        scheduleRafFlush();
      }
    } else if (cursorPreviewRef.current) {
      pendingCursorRef.current = null;
      scheduleRafFlush();
    }
  }, [canComment, placingComment, isDrawing, moving, resizing, pendingPlacement, editingDraft, getPositionFromEvent, scheduleRafFlush]);

  const handlePageMouseUp = useCallback((e) => {
    if (moving) {
      const ms = moveStartRef.current;
      const wasEditing = ms?.editing;
      const movingMarkerId = ms?.movingMarkerId;
      const movingMarker = ms?.movingMarker;
      setMoving(false);
      moveStartRef.current = null;
      if (movingMarkerId && movingMarker) {
        const posX = ms.lastPosX ?? ms.startPosX;
        const posY = ms.lastPosY ?? ms.startPosY;
        if (markerMovedRef.current) {
          persistCommentPosition(movingMarkerId, posX, posY, movingMarker.width, movingMarker.height);
        } else {
          setSelectedCommentId(movingMarker.unique_identifier);
          setEditingDraft({
            content: movingMarker.content,
            position_x: movingMarker.position_x,
            position_y: movingMarker.position_y,
            width: movingMarker.width ?? COMMENT_DEFAULTS.width,
            height: movingMarker.height ?? COMMENT_DEFAULTS.height,
          });
        }
        return;
      }
      if (wasEditing) {
        persistEditingDraft({ closeOnSuccess: false, silent: true });
      }
      return;
    }
    if (resizing) {
      const wasEditing = resizeStartRef.current?.editing;
      setResizing(null);
      resizeStartRef.current = null;
      if (wasEditing) {
        persistEditingDraft({ closeOnSuccess: false, silent: true });
      }
      return;
    }
    if (isDrawing && drawStartRef.current && pageWrapperRef.current) {
      const start = drawStartRef.current;
      const curr = getPositionFromEvent(e);
      if (curr) {
        const posX = Math.min(start.position_x, curr.position_x);
        const posY = Math.min(start.position_y, curr.position_y);
        let w = Math.abs(curr.position_x - start.position_x);
        let h = Math.abs(curr.position_y - start.position_y);
        if (w >= 2 && h >= 2) {
          placeCommentBox(posX, posY, w, h);
        } else {
          placeCommentBox(posX, posY, COMMENT_DEFAULTS.width, COMMENT_DEFAULTS.height);
        }
      }
      drawStartRef.current = null;
      setIsDrawing(false);
      setCursorPreview(null);
    }
  }, [isDrawing, resizing, moving, getPositionFromEvent, placeCommentBox, persistEditingDraft, persistCommentPosition]);

  const handlePageMouseLeave = useCallback(() => {
    if (!draggingCommentRef.current && !isDrawing && !resizing && !moving) {
      setCursorPreview(null);
    }
  }, [isDrawing, resizing, moving]);

  useEffect(() => {
    if (!isDrawing && !resizing && !moving) return;
    const onMove = (e) => handlePageMouseMove(e);
    const onUp = (e) => handlePageMouseUp(e);
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [isDrawing, resizing, moving, handlePageMouseMove, handlePageMouseUp]);

  const handleMarkerMouseDown = useCallback((c, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.target.closest('button, .attachment-preview-comment-postit-reply-btn')) return;
    if (!canComment || commentSubmitting) return;
    if (!canEditComment(c)) return;
    const pos = getPositionFromEvent(e);
    if (!pos) return;
    markerMovedRef.current = false;
    moveStartRef.current = {
      startMouseX: pos.position_x,
      startMouseY: pos.position_y,
      startPosX: c.position_x,
      startPosY: c.position_y,
      movingMarkerId: c.unique_identifier,
      movingMarker: c,
      markerWidth: POSTIT_MOVE_SIZE.width,
      markerHeight: POSTIT_MOVE_SIZE.height,
    };
    setMoving(true);
  }, [canComment, commentSubmitting, canEditComment, getPositionFromEvent]);

  const handlePlaceholderMouseDown = useCallback((e, isEditing = false) => {
    if (e.target.closest('button, .attachment-preview-placeholder-save, .attachment-preview-resize-handle, .ProseMirror')) return;
    e.stopPropagation();
    const target = isEditing ? editingDraft : pendingPlacement;
    if (!target) return;
    if (!isEditing) setSelectedPlacement(true);
    const pos = getPositionFromEvent(e);
    if (pos) {
      moveStartRef.current = {
        startMouseX: pos.position_x,
        startMouseY: pos.position_y,
        startPosX: target.position_x,
        startPosY: target.position_y,
        editing: isEditing,
      };
      setMoving(true);
    }
  }, [getPositionFromEvent, pendingPlacement, editingDraft]);

  const handleResizeHandleMouseDown = useCallback((e, corner, isEditing = false) => {
    e.stopPropagation();
    e.preventDefault();
    setResizing(corner);
    const target = isEditing ? editingDraft : pendingPlacement;
    if (target) {
      resizeStartRef.current = {
        corner,
        startPosX: target.position_x,
        startPosY: target.position_y,
        startW: target.width,
        startH: target.height,
        editing: isEditing,
      };
    }
  }, [pendingPlacement, editingDraft]);

  const handleCommentDragStart = useCallback((e) => {
    if (!canComment || commentSubmitting) {
      e.preventDefault();
      return;
    }
    draggingCommentRef.current = true;
    e.dataTransfer.setData('application/x-comment-marker', 'comment');
    e.dataTransfer.effectAllowed = 'copy';
  }, [canComment, commentSubmitting]);

  const handlePageDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    if (!pageWrapperRef.current || !canComment) return;
    if (!draggingCommentRef.current) return;
    const pos = getPositionFromEvent(e);
    if (pos) {
      setDragPreview({
        ...pos,
        width: COMMENT_DEFAULTS.width,
        height: COMMENT_DEFAULTS.height,
      });
    }
  }, [canComment, getPositionFromEvent]);

  const handlePageDragLeave = useCallback((e) => {
    if (!pageWrapperRef.current?.contains(e.relatedTarget)) {
      setDragPreview(null);
    }
  }, []);

  const handlePageDrop = useCallback((e) => {
    e.preventDefault();
    setDragPreview(null);
    draggingCommentRef.current = false;
    if (!canComment) return;
    const pos = getPositionFromEvent(e);
    if (pos) {
      placeCommentBox(pos.position_x, pos.position_y, COMMENT_DEFAULTS.width, COMMENT_DEFAULTS.height);
      message.success(t('attachmentPreview.positionSet') || 'Position set');
    }
  }, [canComment, getPositionFromEvent, placeCommentBox, t]);

  const handleCommentDragEnd = useCallback(() => {
    draggingCommentRef.current = false;
    setDragPreview(null);
  }, []);

  const defaultPlacement = useMemo(() => ({
    page: currentPage,
    position_x: 50,
    position_y: 50,
    width: COMMENT_DEFAULTS.width,
    height: COMMENT_DEFAULTS.height,
  }), [currentPage]);

  const submitComment = useCallback((content, placement) => {
    const trimmed = typeof content === 'string' ? content.trim() : content;
    if (isContentEmpty(trimmed) || !canComment || commentSubmitting) return;
    const pl = placement || defaultPlacement;
    if (useLocalComments) {
      const newComment = {
        content: trimmed,
        page: pl.page,
        position_x: pl.position_x,
        position_y: pl.position_y,
        width: pl.width,
        height: pl.height,
        unique_identifier: `local-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      };
      onLocalCommentsChange([...(Array.isArray(localComments) ? localComments : []), newComment]);
      setCommentDraft('');
      setPendingPlacement(null);
      setSelectedPlacement(false);
      message.success(t('common.saved') || 'Comment added');
      return;
    }
    if (useDraftFileComments) {
      const newComment = {
        content: trimmed,
        page: pl.page,
        position_x: pl.position_x,
        position_y: pl.position_y,
        width: pl.width,
        height: pl.height,
        unique_identifier: `draft-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      };
      onDraftFileCommentsChange([...(Array.isArray(draftFileComments) ? draftFileComments : []), newComment]);
      setCommentDraft('');
      setPendingPlacement(null);
      setSelectedPlacement(false);
      message.success(t('common.saved') || 'Comment added');
      return;
    }
    setCommentSubmitting(true);
    axios.post('/api/attachment-preview/comments/create/', {
      attachment_id: documentFileId,
      source,
      content: trimmed,
      page: pl.page,
      position_x: pl.position_x,
      position_y: pl.position_y,
      width: pl.width,
      height: pl.height,
    }, { headers: { 'Content-Type': 'application/json' } })
      .then((res) => {
        setComments((prev) => [...prev, { ...res.data.comment, replies: [] }]);
        setCommentDraft('');
        setPendingPlacement(null);
        setSelectedPlacement(false);
        message.success(t('common.saved') || 'Comment added');
      })
      .catch((err) => {
        const msg = err.response?.data?.error || err.message;
        message.error(msg);
      })
      .finally(() => setCommentSubmitting(false));
  }, [canComment, commentSubmitting, documentFileId, source, t, defaultPlacement, useLocalComments, localComments, onLocalCommentsChange, useDraftFileComments, draftFileComments, onDraftFileCommentsChange]);

  const handleAddComment = useCallback(() => {
    submitComment(commentDraft, pendingPlacement || defaultPlacement);
  }, [commentDraft, pendingPlacement, defaultPlacement, submitComment]);

  const addReplyToTree = useCallback((tree, parentId, newReply) => {
    return tree.map((c) => {
      if (c.unique_identifier === parentId) return { ...c, replies: [...(c.replies || []), newReply] };
      if (c.replies?.length) return { ...c, replies: addReplyToTree(c.replies, parentId, newReply) };
      return c;
    });
  }, []);

  const handleAddReply = useCallback((parentCommentId, content) => {
    const trimmed = typeof content === 'string' ? content.trim() : content;
    if (isContentEmpty(trimmed) || !canReplyToComment || commentSubmitting) return;
    const parent = findInTree(comments, parentCommentId);
    if (!parent) return;
    if (useDraftFileComments) {
      const newReply = {
        unique_identifier: `draft-reply-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        parent_comment_id: parentCommentId,
        author_email: currentUserEmail || 'Anonymous',
        content: trimmed,
        page: parent.page ?? 1,
        position_x: 50,
        position_y: 50,
        created_at: new Date().toISOString(),
        replies: [],
      };
      const updated = addReplyToTree(Array.isArray(draftFileComments) ? draftFileComments : [], parentCommentId, newReply);
      setComments(updated);
      onDraftFileCommentsChange(updated);
      setReplyingToCommentId(null);
      setReplyDraft('');
      message.success(t('attachmentPreview.replyAdded') || 'Reply added');
      return;
    }
    if (!documentFileId) return;
    setCommentSubmitting(true);
    const payload = {
      attachment_id: documentFileId,
      source,
      content: trimmed,
      parent_comment_id: parentCommentId,
    };
    axios.post('/api/attachment-preview/comments/create/', payload, { headers: { 'Content-Type': 'application/json' } })
      .then((res) => {
        setComments((prev) => addReplyToTree(prev, parentCommentId, res.data.comment));
        setReplyingToCommentId(null);
        setReplyDraft('');
        message.success(t('attachmentPreview.replyAdded') || 'Reply added');
      })
      .catch((err) => {
        const msg = err.response?.data?.error || err.message;
        message.error(msg);
      })
      .finally(() => setCommentSubmitting(false));
  }, [canReplyToComment, commentSubmitting, documentFileId, source, comments, addReplyToTree, useDraftFileComments, draftFileComments, onDraftFileCommentsChange, currentUserEmail, t]);

  const handleSaveEditedComment = useCallback(() => {
    persistEditingDraft({ closeOnSuccess: true });
  }, [persistEditingDraft]);

  const handleDiscardPlacement = useCallback(() => {
    setPendingPlacement(null);
    setCommentDraft('');
    setSelectedPlacement(false);
  }, []);

  const handleDeleteComment = useCallback(() => {
    if (!selectedCommentId || commentSubmitting) return;
    if (useLocalComments) {
      const filtered = filterFromTree(Array.isArray(localComments) ? localComments : [], selectedCommentId);
      onLocalCommentsChange(filtered);
      setComments(filtered);
      setSelectedCommentId(null);
      setEditingDraft(null);
      message.success(t('common.deleted') || 'Deleted');
      return;
    }
    if (useDraftFileComments) {
      const updated = filterFromTree(Array.isArray(draftFileComments) ? draftFileComments : [], selectedCommentId);
      setComments(updated);
      onDraftFileCommentsChange(updated);
      setSelectedCommentId(null);
      setEditingDraft(null);
      message.success(t('common.deleted') || 'Deleted');
      return;
    }
    const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(selectedCommentId);
    if (!isUuid) return;
    setCommentSubmitting(true);
    axios.delete(`/api/attachment-preview/comments/${selectedCommentId}/delete/`)
      .then(() => {
        setComments((prev) => filterFromTree(prev, selectedCommentId));
        setSelectedCommentId(null);
        setEditingDraft(null);
        message.success(t('common.deleted') || 'Deleted');
      })
      .catch((err) => {
        const msg = err.response?.data?.error || err.message;
        message.error(msg);
      })
      .finally(() => setCommentSubmitting(false));
  }, [selectedCommentId, commentSubmitting, useLocalComments, localComments, onLocalCommentsChange, useDraftFileComments, draftFileComments, onDraftFileCommentsChange, t]);

  const handleSelectComment = useCallback((c) => {
    if (!canEditComment(c)) return;
    const next = selectedCommentId === c.unique_identifier ? null : c.unique_identifier;
    setSelectedCommentId(next);
    if (next) {
      setReplyingToCommentId(null);
      setEditingDraft({
        content: c.content,
        position_x: c.position_x,
        position_y: c.position_y,
        width: c.width ?? COMMENT_DEFAULTS.width,
        height: c.height ?? COMMENT_DEFAULTS.height,
      });
    } else {
      setEditingDraft(null);
    }
  }, [selectedCommentId, canEditComment]);

  const openReplyTo = useCallback((commentId) => {
    const isOpening = replyingToCommentId !== commentId;
    setReplyingToCommentId((prev) => (prev === commentId ? null : commentId));
    setReplyDraft('');
    if (isOpening) {
      setSelectedCommentId(null);
      setEditingDraft(null);
    }
  }, [replyingToCommentId]);

  const handleDownload = useCallback(() => {
    if (onDownload) {
      onDownload();
    } else if (isDraftFile && draftDownloadUrl) {
      const link = document.createElement('a');
      link.href = draftDownloadUrl;
      link.download = fileName || 'download';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      link.remove();
      message.success(t('common.downloaded') || 'Downloaded');
    } else if (isLocalFile && localFile) {
      const file = localFile.originFileObj || localFile;
      const blobUrl = window.URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = file.name || fileName || 'download';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      message.success(t('common.downloaded') || 'Downloaded');
    } else if (documentFileId && fileName) {
      const downloadUrl = source === 'documenso'
        ? `/api/docuso/download/${documentFileId}/`
        : source === 'messages'
        ? `/api/messages/download/${documentFileId}/`
        : `/api/documents/download/${documentFileId}/`;
      axios.get(downloadUrl, { responseType: 'blob' })
        .then((response) => {
          const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(blobUrl);
          message.success(t('common.downloaded') || 'Downloaded');
        })
        .catch(() => message.error(t('common.downloadFailed') || 'Download failed'));
    }
    onClose();
  }, [onDownload, documentFileId, fileName, onClose, t, source, isLocalFile, localFile, isDraftFile, draftDownloadUrl]);

  const renderBody = () => {
    if (loading) {
      return (
        <div className="attachment-preview-loading">
          <Spin size="large" />
        </div>
      );
    }

    if (error) {
      const isUnsupported = error.status === 415;
      const displayMsg = isUnsupported ? (t('attachmentPreview.notAvailable') || 'Preview not available') : (error.message || '');
      return (
        <div className="attachment-preview-error">
          <p>{displayMsg}</p>
          <Button type="primary" icon={<DownloadOutlined />} onClick={handleDownload}>
            {t('common.download') || 'Download'}
          </Button>
        </div>
      );
    }

    if (!pdfData) return null;

    const currentPageComments = comments.filter((c) => Number(c.page) === currentPage);
    const currentPageCommentTree = commentTree.filter((c) => Number(c.page) === currentPage);

    const renderReply = (reply, depth) => {
      const replyAuthorColor = getAuthorColor(reply);
      return (
      <div key={reply.unique_identifier} className={`attachment-preview-thread-reply attachment-preview-thread-reply-depth-${depth}`} style={{ '--reply-accent': replyAuthorColor.accent }}>
        {selectedCommentId === reply.unique_identifier && editingDraft ? (
          <div className="attachment-preview-thread-edit">
            <CommentEditor
              ref={editingInputRef}
              value={editingDraft.content}
              onChange={(html) => updateEditingDraft({ content: html })}
              placeholder={t('attachmentPreview.addComment') || 'Add a comment...'}
              disabled={commentSubmitting}
              toolbarSuffix={
                <>
                  <button type="button" className="attachment-preview-placeholder-close" tabIndex={-1} onClick={() => { setSelectedCommentId(null); setEditingDraft(null); }} disabled={commentSubmitting} title={t('common.close') || 'Close'}>
                    <CloseOutlined />
                  </button>
                  <button type="button" className="attachment-preview-placeholder-trash" tabIndex={-1} onClick={(e) => { e.preventDefault(); handleDeleteComment(); }} disabled={commentSubmitting} title={t('attachmentPreview.deleteComment') || 'Delete comment'}>
                    <DeleteOutlined />
                  </button>
                  <button type="button" className="attachment-preview-placeholder-save" tabIndex={-1} onClick={(e) => { e.preventDefault(); handleSaveEditedComment(); }} disabled={commentSubmitting || isContentEmpty(editingDraft.content)} title={t('attachmentPreview.saveComment') || 'Save comment'}>
                    <ArrowRightOutlined />
                  </button>
                </>
              }
            />
          </div>
        ) : (
          <div className={`attachment-preview-thread-record${!isLoggedIn ? ' attachment-preview-thread-record-no-actions' : ''}`}>
            <div className="attachment-preview-thread-reply-content" dangerouslySetInnerHTML={{ __html: reply.content || '' }} />
            {isLoggedIn && (
              <div className="attachment-preview-thread-actions">
                {canEditComment(reply) && (
                  <button type="button" className="attachment-preview-thread-action-icon" onClick={() => handleSelectComment(reply)} title={t('attachmentPreview.editComment') || 'Edit'} tabIndex={-1}>
                    <EditOutlined />
                  </button>
                )}
                {canReplyToComment && (
                  <button type="button" className="attachment-preview-thread-action-icon" onClick={() => openReplyTo(reply.unique_identifier)} disabled={commentSubmitting} title={t('attachmentPreview.replyToComment') || 'Reply'} tabIndex={-1}>
                    <CommentOutlined />
                  </button>
                )}
              </div>
            )}
          </div>
        )}
        {replyingToCommentId === reply.unique_identifier && (
          <div className={`attachment-preview-thread-reply-form ${depth > 0 ? 'attachment-preview-thread-reply-form-nested' : ''}`}>
            <CommentEditor
              value={replyDraft}
              onChange={setReplyDraft}
              placeholder={t('attachmentPreview.writeReply') || 'Write a reply...'}
              disabled={commentSubmitting}
              toolbarSuffix={
                <>
                  <button type="button" className="attachment-preview-placeholder-trash" tabIndex={-1} onClick={(e) => { e.preventDefault(); setReplyingToCommentId(null); setReplyDraft(''); }} disabled={commentSubmitting} title={t('attachmentPreview.cancelReply') || 'Cancel'}>
                    <CloseOutlined />
                  </button>
                  <button type="button" className="attachment-preview-placeholder-save" tabIndex={-1} onClick={(e) => { e.preventDefault(); handleAddReply(reply.unique_identifier, replyDraft); }} disabled={commentSubmitting || isContentEmpty(replyDraft)} title={t('attachmentPreview.sendReply') || 'Send reply'}>
                    <ArrowRightOutlined />
                  </button>
                </>
              }
            />
          </div>
        )}
        {(reply.replies ?? []).map((nested) => renderReply(nested, depth + 1))}
      </div>
    );
    };

    return (
      <div className={`attachment-preview-content ${minimized ? 'attachment-preview-content-minimized' : ''}`}>
        <div className="attachment-preview-main">
          {canViewComments && sidebarCollapsed && !minimized && (
            <button
              type="button"
              className="attachment-preview-show-comments-btn"
              onClick={() => setSidebarCollapsed(false)}
              title={t('attachmentPreview.showComments') || 'Show comments'}
              tabIndex={-1}
            >
              <MenuUnfoldOutlined />
              <span>{t('attachmentPreview.showComments') || 'Show comments'}</span>
            </button>
          )}
          <div ref={pdfAreaRef} className="attachment-preview-pdf-area">
          <div
            ref={pageWrapperRef}
            className={`attachment-preview-page-wrapper ${placingComment ? 'attachment-preview-page-wrapper-placing' : ''}`}
            onMouseDown={handlePageMouseDown}
            onMouseMove={handlePageMouseMove}
            onMouseLeave={handlePageMouseLeave}
            onDragOver={handlePageDragOver}
            onDragLeave={handlePageDragLeave}
            onDrop={handlePageDrop}
            role={placingComment ? 'button' : undefined}
            style={{ cursor: placingComment ? 'crosshair' : undefined }}
          >
            {(cursorPreview || dragPreview) && (
              <div
                className={dragPreview ? 'attachment-preview-drag-preview' : 'attachment-preview-cursor-preview'}
                style={{
                  left: `${(cursorPreview || dragPreview).position_x}%`,
                  top: `${(cursorPreview || dragPreview).position_y}%`,
                  width: `${(cursorPreview || dragPreview).width ?? COMMENT_DEFAULTS.width}%`,
                  height: `${(cursorPreview || dragPreview).height ?? COMMENT_DEFAULTS.height}%`,
                }}
              />
            )}
            <Document key={pdfData || 'doc'} file={fileConfig} onLoadSuccess={onDocumentLoadSuccess}>
              <Page pageNumber={currentPage} width={pageRenderWidth} renderAnnotationLayer={false} />
            </Document>
            {canViewComments && (
              <div className="attachment-preview-comments-overlay">
                {pendingPlacement && Number(pendingPlacement.page) === currentPage && (
                  <div
                    className={`attachment-preview-comment-placeholder ${selectedPlacement ? 'selected' : ''}`}
                    style={{
                      left: `${pendingPlacement.position_x}%`,
                      top: `${pendingPlacement.position_y}%`,
                      width: `${pendingPlacement.width}%`,
                      height: `${pendingPlacement.height}%`,
                    }}
                    onMouseDown={(e) => handlePlaceholderMouseDown(e, false)}
                    title={t('attachmentPreview.dragToMove') || 'Drag to move'}
                  >
                    <div className="attachment-preview-placeholder-editor" onClick={(e) => e.stopPropagation()}>
                      <CommentEditor
                        ref={placeholderInputRef}
                        value={commentDraft}
                        onChange={setCommentDraft}
                        placeholder={t('attachmentPreview.addComment') || 'Add a comment...'}
                        disabled={commentSubmitting}
                        toolbarSuffix={
                          <>
                            <button
                              type="button"
                              className="attachment-preview-placeholder-trash"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleDiscardPlacement();
                              }}
                              disabled={commentSubmitting}
                              title={t('attachmentPreview.discardComment') || 'Discard'}
                            >
                              <DeleteOutlined />
                            </button>
                            <button
                              type="button"
                              className="attachment-preview-placeholder-save"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleAddComment();
                              }}
                              disabled={commentSubmitting || isContentEmpty(commentDraft)}
                              title={t('attachmentPreview.saveComment') || 'Save comment'}
                            >
                              <ArrowRightOutlined />
                            </button>
                          </>
                        }
                      />
                    </div>
                    {selectedPlacement && (
                      <>
                        {['tl', 'tr', 'bl', 'br'].map((corner) => (
                          <div
                            key={corner}
                            className={`attachment-preview-resize-handle attachment-preview-resize-${corner}`}
                            onMouseDown={(ev) => handleResizeHandleMouseDown(ev, corner, false)}
                          />
                        ))}
                      </>
                    )}
                  </div>
                )}
                {editingDraft && selectedCommentId && (() => {
                  const comment = findInTree(currentPageCommentTree, selectedCommentId);
                  const isTopLevel = comment && !comment.parent_comment_id;
                  if (!comment || !isTopLevel) return null;
                  return (
                    <div
                      className="attachment-preview-comment-placeholder selected"
                      style={{
                        left: `${editingDraft.position_x}%`,
                        top: `${editingDraft.position_y}%`,
                        width: `${editingDraft.width}%`,
                        height: `${editingDraft.height}%`,
                      }}
                      onMouseDown={(e) => handlePlaceholderMouseDown(e, true)}
                      title={t('attachmentPreview.dragToMove') || 'Drag to move'}
                    >
                      <div className="attachment-preview-placeholder-editor" onClick={(e) => e.stopPropagation()}>
                        <CommentEditor
                          ref={editingInputRef}
                          value={editingDraft.content}
                          onChange={(html) => updateEditingDraft({ content: html })}
                          placeholder={t('attachmentPreview.addComment') || 'Add a comment...'}
                          disabled={commentSubmitting}
                          toolbarSuffix={
                            <>
                              <button
                                type="button"
                                className="attachment-preview-placeholder-close"
                                tabIndex={-1}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setSelectedCommentId(null);
                                  setEditingDraft(null);
                                }}
                                disabled={commentSubmitting}
                                title={t('common.close') || 'Close'}
                              >
                                <CloseOutlined />
                              </button>
                            <button
                              type="button"
                              className="attachment-preview-placeholder-trash"
                              tabIndex={-1}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleDeleteComment();
                              }}
                                disabled={commentSubmitting}
                                title={t('attachmentPreview.deleteComment') || 'Delete comment'}
                              >
                                <DeleteOutlined />
                              </button>
                            <button
                              type="button"
                              className="attachment-preview-placeholder-save"
                              tabIndex={-1}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleSaveEditedComment();
                              }}
                                disabled={commentSubmitting || isContentEmpty(editingDraft.content)}
                                title={t('attachmentPreview.saveComment') || 'Save comment'}
                              >
                                <ArrowRightOutlined />
                              </button>
                            </>
                          }
                        />
                      </div>
                      {['tl', 'tr', 'bl', 'br'].map((corner) => (
                        <div
                          key={corner}
                          className={`attachment-preview-resize-handle attachment-preview-resize-${corner}`}
                          onMouseDown={(ev) => handleResizeHandleMouseDown(ev, corner, true)}
                        />
                      ))}
                    </div>
                  );
                })()}
                {postitsVisible && currentPageCommentTree.map((thread) => {
                  const authorColor = getAuthorColor(thread);
                  return (
                  <div key={thread.unique_identifier} className={`attachment-preview-comment-thread${replyingToCommentId === thread.unique_identifier ? ' attachment-preview-comment-thread-replying' : ''}`}>
                    {selectedCommentId !== thread.unique_identifier && (
                      <div
                        className="attachment-preview-comment-postit-wrapper"
                        style={{
                          left: `${thread.position_x ?? 50}%`,
                          top: `${thread.position_y ?? 50}%`,
                          width: `${thread.width ?? COMMENT_DEFAULTS.width}%`,
                          height: `${thread.height ?? COMMENT_DEFAULTS.height}%`,
                        }}
                      >
                        <div
                          className={`attachment-preview-comment-postit${!canEditComment(thread) ? ' attachment-preview-comment-postit-readonly' : ''}`}
                          style={{
                            cursor: canEditComment(thread) ? undefined : 'default',
                            '--postit-border': authorColor.border,
                            '--postit-accent': authorColor.accent,
                          }}
                          onMouseDown={(e) => {
                            e.stopPropagation();
                            if (canEditComment(thread)) handleMarkerMouseDown(thread, e);
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (commentsPanelRef.current) {
                              const el = threadRefsMap.current[thread.unique_identifier];
                              if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                            }
                          }}
                        >
                          {renderThreadContent(thread, authorColor.border)}
                          {canComment && (
                            <button
                              type="button"
                              className="attachment-preview-comment-postit-reply-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                openReplyTo(thread.unique_identifier);
                              }}
                              title={t('attachmentPreview.replyToComment') || 'Reply'}
                              tabIndex={-1}
                            >
                              <CommentOutlined />
                              <span>{t('attachmentPreview.reply') || 'Reply'}</span>
                            </button>
                          )}
                        </div>
                        {replyingToCommentId === thread.unique_identifier && (
                          <div className="attachment-preview-comment-postit-reply-form">
                            <CommentEditor
                              value={replyDraft}
                              onChange={setReplyDraft}
                              placeholder={t('attachmentPreview.writeReply') || 'Write a reply...'}
                              disabled={commentSubmitting}
                              toolbarSuffix={
                                <>
                                  <button type="button" className="attachment-preview-placeholder-trash" tabIndex={-1} onClick={(e) => { e.preventDefault(); e.stopPropagation(); setReplyingToCommentId(null); setReplyDraft(''); }} disabled={commentSubmitting} title={t('attachmentPreview.cancelReply') || 'Cancel'}>
                                    <CloseOutlined />
                                  </button>
                                  <button type="button" className="attachment-preview-placeholder-save" tabIndex={-1} onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleAddReply(thread.unique_identifier, replyDraft); }} disabled={commentSubmitting || isContentEmpty(replyDraft)} title={t('attachmentPreview.sendReply') || 'Send reply'}>
                                    <ArrowRightOutlined />
                                  </button>
                                </>
                              }
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
                })}
              </div>
            )}
          </div>
          </div>
          {canViewComments && (
            <div className={`attachment-preview-comments-panel ${sidebarCollapsed ? 'attachment-preview-comments-panel-collapsed' : ''}`} ref={commentsPanelRef}>
              <div className="attachment-preview-comments-panel-header">
                <span className="attachment-preview-comments-panel-title">
                  {t('attachmentPreview.comments') || 'Comments'}
                  {commentTree.length > 0 && (
                    <span className="attachment-preview-comments-count"> ({commentTree.length})</span>
                  )}
                </span>
                <button
                  type="button"
                  className="attachment-preview-comments-panel-close" tabIndex={-1}
                  onClick={() => setSidebarCollapsed(true)}
                  title={t('attachmentPreview.hideComments') || 'Hide comments'}
                >
                  <MenuFoldOutlined />
                </button>
              </div>
              {!sidebarCollapsed && (
                <div className="attachment-preview-threads">
                    {commentTree.length === 0 && !pendingPlacement && (
                      <div className="attachment-preview-threads-empty">
                        {canComment
                          ? (t('attachmentPreview.noCommentsYet') || 'No comments yet. Click "Add comment" to place one on the document.')
                          : (t('attachmentPreview.noComments') || 'No comments yet.')}
                      </div>
                    )}
                    {commentTree.map((thread) => {
                      const threadAuthorColor = getAuthorColor(thread);
                      return (
                      <div
                        key={thread.unique_identifier}
                        ref={(el) => { if (el) threadRefsMap.current[thread.unique_identifier] = el; }}
                        className={`attachment-preview-thread-card ${selectedCommentId === thread.unique_identifier ? 'attachment-preview-thread-card-selected' : ''}`}
                        style={{ '--thread-accent': threadAuthorColor.accent }}
                      >
                        <div className="attachment-preview-thread-parent">
                          {selectedCommentId === thread.unique_identifier && editingDraft ? (
                            <div className="attachment-preview-thread-edit">
                              <CommentEditor
                                ref={editingInputRef}
                                value={editingDraft.content}
                                onChange={(html) => updateEditingDraft({ content: html })}
                                placeholder={t('attachmentPreview.addComment') || 'Add a comment...'}
                                disabled={commentSubmitting}
                                toolbarSuffix={
                                  <>
                                    <button type="button" className="attachment-preview-placeholder-close" tabIndex={-1} onClick={() => { setSelectedCommentId(null); setEditingDraft(null); }} disabled={commentSubmitting} title={t('common.close') || 'Close'}>
                                      <CloseOutlined />
                                    </button>
                                    <button type="button" className="attachment-preview-placeholder-trash" tabIndex={-1} onClick={(e) => { e.preventDefault(); handleDeleteComment(); }} disabled={commentSubmitting} title={t('attachmentPreview.deleteComment') || 'Delete comment'}>
                                      <DeleteOutlined />
                                    </button>
                                    <button type="button" className="attachment-preview-placeholder-save" tabIndex={-1} onClick={(e) => { e.preventDefault(); handleSaveEditedComment(); }} disabled={commentSubmitting || isContentEmpty(editingDraft.content)} title={t('attachmentPreview.saveComment') || 'Save comment'}>
                                      <ArrowRightOutlined />
                                    </button>
                                  </>
                                }
                              />
                            </div>
                          ) : (
                            <div className={`attachment-preview-thread-record${!isLoggedIn ? ' attachment-preview-thread-record-no-actions' : ''}`}>
                              <div className="attachment-preview-thread-content" dangerouslySetInnerHTML={{ __html: thread.content || '' }} />
                              {isLoggedIn && (
                                <div className="attachment-preview-thread-actions">
                                  {canEditComment(thread) && (
                                    <button type="button" className="attachment-preview-thread-action-icon" onClick={() => handleSelectComment(thread)} title={t('attachmentPreview.editComment') || 'Edit'} tabIndex={-1}>
                                      <EditOutlined />
                                    </button>
                                  )}
                                  {canReplyToComment && (
                                    <button type="button" className="attachment-preview-thread-action-icon" onClick={() => openReplyTo(thread.unique_identifier)} disabled={commentSubmitting} title={t('attachmentPreview.replyToComment') || 'Reply'} tabIndex={-1}>
                                      <CommentOutlined />
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        {replyingToCommentId === thread.unique_identifier && (
                          <div className="attachment-preview-thread-reply-form">
                            <CommentEditor
                              value={replyDraft}
                              onChange={setReplyDraft}
                              placeholder={t('attachmentPreview.writeReply') || 'Write a reply...'}
                              disabled={commentSubmitting}
                              toolbarSuffix={
                                <>
                                  <button type="button" className="attachment-preview-placeholder-trash" tabIndex={-1} onClick={(e) => { e.preventDefault(); setReplyingToCommentId(null); setReplyDraft(''); }} disabled={commentSubmitting} title={t('attachmentPreview.cancelReply') || 'Cancel'}>
                                    <CloseOutlined />
                                  </button>
                                  <button type="button" className="attachment-preview-placeholder-save" tabIndex={-1} onClick={(e) => { e.preventDefault(); handleAddReply(thread.unique_identifier, replyDraft); }} disabled={commentSubmitting || isContentEmpty(replyDraft)} title={t('attachmentPreview.sendReply') || 'Send reply'}>
                                    <ArrowRightOutlined />
                                  </button>
                                </>
                              }
                            />
                          </div>
                        )}
                        {(thread.replies ?? []).map((reply) => renderReply(reply, 0))}
                      </div>
                    );
                    })}
                  </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const canMinimize = !!pdfData && !loading && !error && !!pageDimensions;
  const HEADER_HEIGHT = 55;
  const BODY_PADDING_H = 32;
  const BODY_PADDING_V = 32;
  const PAGINATION_HEIGHT = numPages > 1 ? 52 : 0;
  const minimizedContentWidth = pageDimensions
    ? Math.min(Math.ceil(pageDimensions.width) + BODY_PADDING_H + 10, window.innerWidth * 0.95)
    : 800;
  const minimizedContentHeight = pageDimensions
    ? Math.min(
        Math.ceil(pageDimensions.height) + HEADER_HEIGHT + PAGINATION_HEIGHT + BODY_PADDING_V,
        window.innerHeight * 0.95
      )
    : 600;

  const modalStyles = minimized && canMinimize
    ? {
        wrapper: { width: '100vw', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' },
        content: {
          width: minimizedContentWidth,
          height: minimizedContentHeight,
          maxWidth: '95vw',
          maxHeight: '95vh',
          display: 'flex',
          flexDirection: 'column',
          margin: 0
        },
        body: { flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', padding: 16 }
      }
    : {
        wrapper: { height: '100vh', alignItems: 'flex-start' },
        content: { height: '100vh', maxHeight: '100vh', display: 'flex', flexDirection: 'column', width: '95vw' },
        body: { flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }
      };

  const modalHeader = (
    <div className="attachment-preview-modal-header" onClick={(e) => e.stopPropagation()}>
      <span className="attachment-preview-filename">{displayFileName}</span>
      <div className="attachment-preview-header-controls">
        {numPages != null && numPages > 1 && (
          <div className="attachment-preview-pagination-inline">
            <Button
              size="small"
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              tabIndex={-1}
            >
              {t('common.previous') || 'Previous'}
            </Button>
            <span className="attachment-preview-page-info">
              {currentPage} / {numPages}
            </span>
            <Button
              size="small"
              disabled={currentPage >= numPages}
              onClick={() => setCurrentPage((p) => Math.min(numPages, p + 1))}
              tabIndex={-1}
            >
              {t('common.next') || 'Next'}
            </Button>
          </div>
        )}
        {numPages != null && (
          <div className="attachment-preview-zoom attachment-preview-zoom-header">
            <Tooltip title={t('documenso.zoomOut') || 'Zoom out (Ctrl+scroll)'}>
              <Button
                size="small"
                icon={<ZoomOutOutlined />}
                onClick={() => handleZoom(-ZOOM_STEP)}
                disabled={zoomLevel <= ZOOM_MIN}
                tabIndex={-1}
              />
            </Tooltip>
            <span className="attachment-preview-zoom-level">{Math.round(zoomLevel * 100)}%</span>
            <Tooltip title={t('documenso.zoomIn') || 'Zoom in (Ctrl+scroll)'}>
              <Button
                size="small"
                icon={<ZoomInOutlined />}
                onClick={() => handleZoom(ZOOM_STEP)}
                disabled={zoomLevel >= ZOOM_MAX}
                tabIndex={-1}
              />
            </Tooltip>
          </div>
        )}
        {canComment && (
          <div className={`attachment-preview-comment-chip-segmented${commentSubmitting ? ' disabled' : ''}`}>
            <div
              className={`attachment-preview-comment-chip${placingComment ? ' selected' : ''}${commentSubmitting ? ' disabled' : ''}`}
              onClick={() => { if (!commentSubmitting) setPlacingComment((p) => !p); }}
              draggable={!commentSubmitting}
              onDragStart={handleCommentDragStart}
              onDragEnd={handleCommentDragEnd}
              tabIndex={-1}
            >
              <span>{t('attachmentPreview.addCommentLabel') || 'Add comment'}</span>
            </div>
          </div>
        )}
        {canViewComments && commentTree.length > 0 && (
          <button
            type="button"
            className={`attachment-preview-header-btn attachment-preview-postits-toggle${postitsVisible ? ' active' : ''}`}
            onClick={() => setPostitsVisible((v) => !v)}
            title={postitsVisible ? (t('attachmentPreview.hidePostits') || 'Hide comment markers') : (t('attachmentPreview.showPostits') || 'Show comment markers')}
            tabIndex={-1}
          >
            {postitsVisible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
          </button>
        )}
        <div className="attachment-preview-header-icon-btns">
          {canMinimize && (
            <button
              type="button"
              className="attachment-preview-header-btn"
              onClick={() => setMinimized((m) => !m)}
              title={minimized ? 'Maximize' : 'Minimize'}
              tabIndex={-1}
            >
              {minimized ? <ExpandOutlined /> : <CompressOutlined />}
            </button>
          )}
          <button
            type="button"
            className="attachment-preview-header-btn"
            onClick={onClose}
            title="Close"
            tabIndex={-1}
          >
            <CloseOutlined />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={minimized && canMinimize ? minimizedContentWidth : '95vw'}
      destroyOnClose
      centered={minimized && canMinimize}
      styles={modalStyles}
      title={modalHeader}
      closeIcon={null}
      className={`attachment-preview-modal ${minimized ? 'attachment-preview-modal-minimized' : ''}`}
    >
      {renderBody()}
    </Modal>
  );
};

export default AttachmentPreview;
